import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Brain, MessageCircle, Save, RotateCcw, Info, AlertTriangle, Smartphone, Globe,
  Loader2, Plus, Trash2, ArrowUp, ArrowDown, X, Bot, Lock,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

// Stable internal row id (never sent to the backend). Used for React keys and the
// accordion value so that editing an intent's user-facing ID does not remount the row.
let _uidCounter = 0;
const genUid = () => `iu_${Date.now()}_${_uidCounter++}`;

export default function ThinkingLayer() {
  const [actors, setActors] = useState([]);
  const [selectedActorId, setSelectedActorId] = useState("");
  const [config, setConfig] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const [fallbackSearch, setFallbackSearch] = useState("");
  const [keywordDrafts, setKeywordDrafts] = useState({});
  const [openItems, setOpenItems] = useState([]);

  useEffect(() => {
    fetchActors();
    fetchModels();
  }, []);

  const fetchActors = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/actors`, { headers: authHeaders() });
      const list = res.data || [];
      setActors(list);
      if (list.length > 0) {
        setSelectedActorId(list[0].id);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error("Error fetching actors:", e);
      toast.error("Failed to load AI actors");
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      setLoadingModels(true);
      const res = await axios.get(`${BACKEND_URL}/api/openrouter/models`);
      setOpenrouterModels(res.data.models || []);
    } catch (e) {
      console.error("Error fetching models:", e);
    } finally {
      setLoadingModels(false);
    }
  };

  const fetchConfig = useCallback(async (actorId) => {
    if (!actorId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/admin/thinking-layer`, {
        headers: authHeaders(),
        params: { actor_id: actorId },
      });
      const intents = (res.data.config.intents || []).map((i) => ({ ...i, _uid: i._uid || genUid() }));
      setConfig({ ...res.data.config, intents, actor_id: actorId });
      setActions(res.data.actions || []);
      setOpenItems([]);
      setHasChanges(false);
    } catch (e) {
      console.error("Error fetching thinking layer config:", e);
      toast.error("Failed to load thinking layer configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedActorId) fetchConfig(selectedActorId);
  }, [selectedActorId, fetchConfig]);

  const handleSave = async () => {
    if (!selectedActorId) {
      toast.error("Select an actor first");
      return;
    }
    try {
      setSaving(true);
      await axios.put(
        `${BACKEND_URL}/api/admin/thinking-layer`,
        {
          actor_id: selectedActorId,
          intents: config.intents,
          custom_rules: config.custom_rules || [],
          global_rules: config.global_rules || [],
          thinking_model: config.thinking_model || "",
          thinking_fallback_model: config.thinking_fallback_model || "",
        },
        { headers: authHeaders() }
      );
      toast.success("Thinking layer configuration saved");
      setHasChanges(false);
    } catch (e) {
      console.error("Error saving thinking layer config:", e);
      toast.error(e?.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!selectedActorId) return;
    if (!window.confirm("Reset this actor's thinking layer to the default template? This cannot be undone.")) return;
    try {
      setResetting(true);
      await axios.post(`${BACKEND_URL}/api/admin/thinking-layer/reset`, null, {
        headers: authHeaders(),
        params: { actor_id: selectedActorId },
      });
      toast.success("Configuration reset to defaults");
      await fetchConfig(selectedActorId);
    } catch (e) {
      console.error("Error resetting thinking layer config:", e);
      toast.error("Failed to reset configuration");
    } finally {
      setResetting(false);
    }
  };

  // ---- intent helpers ----
  const mutateIntents = (fn) => {
    setConfig((prev) => ({ ...prev, intents: fn(prev.intents || []) }));
    setHasChanges(true);
  };

  const updateIntent = (uid, field, value) =>
    mutateIntents((intents) => intents.map((i) => (i._uid === uid ? { ...i, [field]: value } : i)));

  const addIntent = () => {
    const uid = genUid();
    mutateIntents((intents) => [
      ...intents,
      {
        _uid: uid,
        id: `CUSTOM_INTENT_${Date.now()}`,
        name: "New Intent",
        description: "",
        conditions: "",
        examples: [],
        spanish_examples: [],
        fallback_keywords: [],
        action: "knowledge_base",
        action_config: {},
        is_active: true,
        is_default: false,
        requires_verification: false,
        include_verified_context: false,
        grounding: "auto",
        response_instructions: "",
        order: (intents.reduce((m, i) => Math.max(m, i.order || 0), 0) || 0) + 1,
      },
    ]);
    setOpenItems((prev) => [...prev, uid]);
  };

  const deleteIntent = (uid) => {
    mutateIntents((intents) => intents.filter((i) => i._uid !== uid));
    setOpenItems((prev) => prev.filter((v) => v !== uid));
  };

  const moveIntent = (uid, dir) =>
    mutateIntents((intents) => {
      const sorted = [...intents].sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = sorted.findIndex((i) => i._uid === uid);
      const swap = idx + dir;
      if (idx < 0 || swap < 0 || swap >= sorted.length) return intents;
      const a = sorted[idx], b = sorted[swap];
      const ao = a.order || 0, bo = b.order || 0;
      return intents.map((i) => {
        if (i._uid === a._uid) return { ...i, order: bo };
        if (i._uid === b._uid) return { ...i, order: ao };
        return i;
      });
    });

  const setDefaultIntent = (uid, checked) =>
    mutateIntents((intents) =>
      intents.map((i) => ({ ...i, is_default: i._uid === uid ? checked : checked ? false : i.is_default }))
    );

  const addExample = (uid, type) => {
    const field = type === "spanish" ? "spanish_examples" : "examples";
    updateIntent(uid, field, [...(config.intents.find((i) => i._uid === uid)?.[field] || []), ""]);
  };
  const updateExample = (uid, type, index, value) => {
    const field = type === "spanish" ? "spanish_examples" : "examples";
    const arr = [...(config.intents.find((i) => i._uid === uid)?.[field] || [])];
    arr[index] = value;
    updateIntent(uid, field, arr);
  };
  const removeExample = (uid, type, index) => {
    const field = type === "spanish" ? "spanish_examples" : "examples";
    const arr = (config.intents.find((i) => i._uid === uid)?.[field] || []).filter((_, i) => i !== index);
    updateIntent(uid, field, arr);
  };

  const addKeyword = (uid) => {
    const draft = (keywordDrafts[uid] || "").trim();
    if (!draft) return;
    const existing = config.intents.find((i) => i._uid === uid)?.fallback_keywords || [];
    if (existing.includes(draft)) {
      setKeywordDrafts((d) => ({ ...d, [uid]: "" }));
      return;
    }
    updateIntent(uid, "fallback_keywords", [...existing, draft]);
    setKeywordDrafts((d) => ({ ...d, [uid]: "" }));
  };
  const removeKeyword = (uid, kw) => {
    const arr = (config.intents.find((i) => i._uid === uid)?.fallback_keywords || []).filter((k) => k !== kw);
    updateIntent(uid, "fallback_keywords", arr);
  };

  // ---- global rules ----
  const addRule = () =>
    setConfig((p) => { setHasChanges(true); return { ...p, global_rules: [...(p.global_rules || []), ""] }; });
  const updateRule = (idx, val) =>
    setConfig((p) => {
      setHasChanges(true);
      const arr = [...(p.global_rules || [])]; arr[idx] = val; return { ...p, global_rules: arr };
    });
  const removeRule = (idx) =>
    setConfig((p) => {
      setHasChanges(true);
      return { ...p, global_rules: (p.global_rules || []).filter((_, i) => i !== idx) };
    });

  const actionLabel = (key) => actions.find((a) => a.key === key)?.label || key;
  const sortedIntents = [...(config?.intents || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 pb-32" data-testid="thinking-layer-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Thinking Layer</h1>
            <p className="text-sm text-slate-500">Per-actor message intent configuration</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={resetting || !selectedActorId} data-testid="reset-button">
            {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges} data-testid="save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Actor selector */}
      <Card data-testid="actor-selector-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Bot className="h-5 w-5 text-indigo-600" />
            <label className="text-sm font-medium text-slate-700">AI Actor</label>
            <Select value={selectedActorId} onValueChange={setSelectedActorId}>
              <SelectTrigger className="w-[320px]" data-testid="actor-select-trigger">
                <SelectValue placeholder="Select an actor" />
              </SelectTrigger>
              <SelectContent>
                {actors.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`actor-option-${a.id}`}>
                    {a.name}{a.actor_functions?.includes("support") ? "  (Support)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">This configuration applies only to the selected actor.</span>
          </div>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">How the Thinking Layer Works</AlertTitle>
        <AlertDescription className="text-blue-700">
          <p className="mb-3">
            Every incoming message is analyzed to determine the user's language and intent, then routed to the
            matched intent's <strong>Action</strong>. If the AI model is unavailable, the keyword fallback uses the
            keywords you define per intent.
          </p>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2"><Globe className="h-4 w-4" /><span className="text-sm font-medium">AI Public Chat Widget</span></div>
            <div className="flex items-center gap-2"><Smartphone className="h-4 w-4" /><span className="text-sm font-medium">WhatsApp Chat</span></div>
          </div>
        </AlertDescription>
      </Alert>

      {/* Config-driven flow preview */}
      <Card data-testid="flow-preview-card">
        <CardHeader><CardTitle className="text-lg">Live Flow (from your config)</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg"><MessageCircle className="h-4 w-4 text-slate-600" /><span>User Message</span></div>
            <span className="text-slate-400">→</span>
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-100 rounded-lg border-2 border-indigo-300"><Brain className="h-4 w-4 text-indigo-600" /><span className="font-medium text-indigo-700">Thinking Layer (LLM)</span></div>
            <span className="text-slate-400">→ matched intent → its Action</span>
          </div>
          <div className="space-y-1">
            {sortedIntents.filter((i) => i.is_active).map((i) => (
              <div key={i.id} className="flex items-center gap-2 text-xs" data-testid={`flow-row-${i.id}`}>
                <span className="font-mono text-slate-700 min-w-[180px]">{i.id}</span>
                <span className="text-slate-400">→</span>
                <Badge variant="outline" className="text-indigo-700 border-indigo-200 bg-indigo-50">{actionLabel(i.action)}</Badge>
                {i.is_default && <Badge variant="secondary" className="text-amber-700 bg-amber-50">default</Badge>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Model Configuration */}
      <Card data-testid="thinking-layer-model-config">
        <CardHeader>
          <CardTitle className="text-lg">Model Configuration</CardTitle>
          <CardDescription>A fast, cost-effective model is recommended since this runs on every message.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Primary Model</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={loadingModels ? "Loading models..." : "Search models..."}
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="thinking-model-search"
                />
                <div className="text-xs text-gray-500 mt-1">Current: <strong>{config?.thinking_model || "google/gemini-2.0-flash-001 (default)"}</strong></div>
                {modelSearch && openrouterModels.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {openrouterModels
                      .filter((m) => m.id?.toLowerCase().includes(modelSearch.toLowerCase()) || m.name?.toLowerCase().includes(modelSearch.toLowerCase()))
                      .slice(0, 20)
                      .map((m) => (
                        <div key={m.id} className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b last:border-0"
                          onClick={() => { setConfig((p) => ({ ...p, thinking_model: m.id })); setModelSearch(""); setHasChanges(true); }}>
                          <div className="font-medium">{m.name || m.id}</div>
                          <div className="text-gray-400">{m.id}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Fallback Model</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search fallback model..."
                  value={fallbackSearch}
                  onChange={(e) => setFallbackSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="thinking-fallback-model-search"
                />
                <div className="text-xs text-gray-500 mt-1">Current: <strong>{config?.thinking_fallback_model || "None (keyword fallback)"}</strong></div>
                {fallbackSearch && openrouterModels.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {openrouterModels
                      .filter((m) => m.id?.toLowerCase().includes(fallbackSearch.toLowerCase()) || m.name?.toLowerCase().includes(fallbackSearch.toLowerCase()))
                      .slice(0, 20)
                      .map((m) => (
                        <div key={m.id} className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b last:border-0"
                          onClick={() => { setConfig((p) => ({ ...p, thinking_fallback_model: m.id })); setFallbackSearch(""); setHasChanges(true); }}>
                          <div className="font-medium">{m.name || m.id}</div>
                          <div className="text-gray-400">{m.id}</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Global Rules */}
      <Card data-testid="global-rules-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Global Rules</CardTitle>
              <CardDescription>High-level disambiguation rules added to the classifier prompt.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addRule} data-testid="add-rule-btn"><Plus className="h-4 w-4 mr-1" /> Add Rule</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {(config?.global_rules || []).length === 0 && <p className="text-sm text-slate-400">No global rules.</p>}
          {(config?.global_rules || []).map((rule, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <Textarea value={rule} onChange={(e) => updateRule(idx, e.target.value)} className="flex-1 min-h-[44px]"
                data-testid={`global-rule-${idx}`} placeholder="Rule..." />
              <Button variant="ghost" size="sm" onClick={() => removeRule(idx)} data-testid={`remove-rule-${idx}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Intent Definitions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Intent Definitions
                <Badge variant="secondary" data-testid="intent-count">{config?.intents?.length || 0} intents</Badge>
              </CardTitle>
              <CardDescription>Each intent routes to an Action. Add fallback keywords used when the AI model is down.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addIntent} data-testid="add-intent-btn"><Plus className="h-4 w-4 mr-1" /> Add Intent</Button>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="w-full">
            {sortedIntents.map((intent) => (
              <AccordionItem key={intent._uid} value={intent._uid} className="border rounded-lg mb-2 px-4" data-testid={`intent-item-${intent.id}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge variant={intent.is_active ? "default" : "secondary"} className={intent.action?.startsWith("booking") ? "bg-green-600" : intent.action === "knowledge_base" ? "bg-blue-600" : "bg-slate-600"}>
                      {intent.id}
                    </Badge>
                    <span className="font-medium text-slate-700">{intent.name}</span>
                    <Badge variant="outline" className="text-indigo-600 border-indigo-200 bg-indigo-50">{actionLabel(intent.action)}</Badge>
                    {intent.is_default && <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">Default</Badge>}
                    {intent.requires_verification && <Badge variant="outline" className="text-rose-600 border-rose-200 bg-rose-50" data-testid={`badge-verify-${intent.id}`}>🔒 Verify</Badge>}
                    {(() => {
                      const g = intent.grounding && intent.grounding !== "auto"
                        ? intent.grounding
                        : (["zoho_context", "zoho_tickets"].includes(intent.action) ? "zoho_only" : intent.action === "knowledge_base" ? "kb_only" : "open");
                      const map = { kb_only: ["📄 Docs", "text-blue-600 border-blue-200 bg-blue-50"], zoho_only: ["🟣 Zoho", "text-purple-600 border-purple-200 bg-purple-50"], open: ["🟢 Open", "text-emerald-600 border-emerald-200 bg-emerald-50"] };
                      const [lbl, cls] = map[g] || map.open;
                      return <Badge variant="outline" className={cls} data-testid={`badge-grounding-${intent.id}`}>{lbl}</Badge>;
                    })()}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 space-y-4">
                  {/* Top controls */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => moveIntent(intent._uid, -1)} data-testid={`move-up-${intent.id}`}><ArrowUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => moveIntent(intent._uid, 1)} data-testid={`move-down-${intent.id}`}><ArrowDown className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2"><span className="text-sm text-slate-600">Active</span>
                        <Switch checked={intent.is_active} onCheckedChange={(c) => updateIntent(intent._uid, "is_active", c)} data-testid={`toggle-${intent.id}`} />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteIntent(intent._uid)} data-testid={`delete-intent-${intent.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>

                  {/* Id + Name */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-slate-700">Intent ID</label>
                      <Input value={intent.id} onChange={(e) => updateIntent(intent._uid, "id", e.target.value)} data-testid={`id-${intent.id}`} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-slate-700">Name</label>
                      <Input value={intent.name} onChange={(e) => updateIntent(intent._uid, "name", e.target.value)} data-testid={`name-${intent.id}`} />
                    </div>
                  </div>

                  {/* Action */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-700">Action</label>
                    <Select value={intent.action} onValueChange={(v) => updateIntent(intent._uid, "action", v)}>
                      <SelectTrigger data-testid={`action-${intent.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {actions.map((a) => (
                          <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Grounding & Verification */}
                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-slate-500" />
                      <span className="text-sm font-semibold text-slate-700">Grounding &amp; Verification</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-700">Requires client verification</p>
                        <p className="text-xs text-slate-400">Block this intent until the client is verified in Zoho.</p>
                      </div>
                      <Switch
                        checked={!!intent.requires_verification}
                        onCheckedChange={(c) => updateIntent(intent._uid, "requires_verification", c)}
                        data-testid={`requires-verification-${intent.id}`}
                      />
                    </div>

                    {intent.requires_verification && (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-700">Include verified customer data</p>
                          <p className="text-xs text-slate-400">Inject the customer's identity (and Zoho data for Zoho actions).</p>
                        </div>
                        <Switch
                          checked={!!intent.include_verified_context}
                          onCheckedChange={(c) => updateIntent(intent._uid, "include_verified_context", c)}
                          data-testid={`include-verified-context-${intent.id}`}
                        />
                      </div>
                    )}

                    <div className="space-y-1">
                      <label className="text-sm text-slate-700">Answer source</label>
                      <Select value={intent.grounding || "auto"} onValueChange={(v) => updateIntent(intent._uid, "grounding", v)}>
                        <SelectTrigger data-testid={`grounding-${intent.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (derive from action)</SelectItem>
                          <SelectItem value="kb_only">Documents only (Knowledge Base)</SelectItem>
                          <SelectItem value="zoho_only">Zoho data only</SelectItem>
                          <SelectItem value="open">Open (no source restriction)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm text-slate-700">Response instructions <span className="text-xs text-slate-400">(prompt hardening — leave blank for safe default)</span></label>
                      <Textarea
                        value={intent.response_instructions || ""}
                        onChange={(e) => updateIntent(intent._uid, "response_instructions", e.target.value)}
                        className="min-h-[70px]"
                        placeholder="e.g. Answer ONLY from the documents below. If it isn't there, say you don't have it. The profile only sets the tone."
                        data-testid={`response-instructions-${intent.id}`}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Description</label>
                    <Textarea value={intent.description} onChange={(e) => updateIntent(intent._uid, "description", e.target.value)} className="min-h-[70px]" data-testid={`description-${intent.id}`} />
                  </div>

                  {/* Conditions */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Conditions</label>
                    <Input value={intent.conditions} onChange={(e) => updateIntent(intent._uid, "conditions", e.target.value)} data-testid={`conditions-${intent.id}`} />
                  </div>

                  {/* Fallback keywords */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Fallback Keywords <span className="text-xs text-slate-400">(used only when the AI model is down)</span></label>
                    <div className="flex flex-wrap gap-2">
                      {(intent.fallback_keywords || []).map((kw) => (
                        <span key={kw} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs" data-testid={`keyword-chip-${intent.id}-${kw}`}>
                          {kw}
                          <button onClick={() => removeKeyword(intent._uid, kw)} data-testid={`remove-keyword-${intent.id}-${kw}`}><X className="h-3 w-3 text-slate-500 hover:text-red-500" /></button>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={keywordDrafts[intent._uid] || ""}
                        onChange={(e) => setKeywordDrafts((d) => ({ ...d, [intent._uid]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(intent._uid); } }}
                        placeholder="Add keyword and press Enter"
                        className="flex-1"
                        data-testid={`keyword-input-${intent.id}`}
                      />
                      <Button variant="outline" size="sm" onClick={() => addKeyword(intent._uid)} data-testid={`add-keyword-${intent.id}`}><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  {/* Examples EN */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">English Examples</label>
                      <Button variant="ghost" size="sm" onClick={() => addExample(intent._uid, "english")}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                    </div>
                    {(intent.examples || []).map((ex, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input value={ex} onChange={(e) => updateExample(intent._uid, "english", idx, e.target.value)} className="flex-1" placeholder="Example message..." />
                        <Button variant="ghost" size="sm" onClick={() => removeExample(intent._uid, "english", idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    ))}
                  </div>

                  {/* Examples ES */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">Spanish Examples</label>
                      <Button variant="ghost" size="sm" onClick={() => addExample(intent._uid, "spanish")}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                    </div>
                    {(intent.spanish_examples || []).map((ex, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Input value={ex} onChange={(e) => updateExample(intent._uid, "spanish", idx, e.target.value)} className="flex-1" placeholder="Ejemplo..." />
                        <Button variant="ghost" size="sm" onClick={() => removeExample(intent._uid, "spanish", idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    ))}
                  </div>

                  {/* Default toggle */}
                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="text-sm text-slate-600">Default intent (catch-all when nothing else matches)</span>
                    <Switch checked={!!intent.is_default} onCheckedChange={(c) => setDefaultIntent(intent._uid, c)} data-testid={`default-toggle-${intent.id}`} />
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {hasChanges && (
        <div className="fixed bottom-6 right-6 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2" data-testid="unsaved-indicator">
          <AlertTriangle className="h-4 w-4" />
          <span>You have unsaved changes</span>
          <Button size="sm" variant="secondary" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Now"}
          </Button>
        </div>
      )}
    </div>
  );
}
